import { useEffect } from "react";
import type { ChatMessage } from "shared";
import { connectSocket } from "../lib/socket";
import { audio } from "../lib/audio";
import { api } from "../lib/api";
import {
  externalPuzzleUrl,
  defaultPuzzleUrl,
  secondaryDefaultPuzzleUrl,
} from "../lib/puzzleUrl";
import { useGame, toUiMessage } from "../store";
import { installBridge } from "./bridge";

interface Args {
  groupId: string;
  userId: string;
  themeId: string;
}

/** Normalise the varied auto_open_pdf payloads into a page list. */
function pdfPages(file: unknown): string[] {
  if (typeof file === "string") return [file];
  if (file && typeof file === "object" && "pages" in file) {
    const pages = (file as { pages: unknown }).pages;
    if (Array.isArray(pages)) return pages as string[];
  }
  return [];
}

/**
 * Connects the socket, joins the room (+DRM presence room), and wires every
 * server->client event to the store / audio manager. Ports js/components/
 * connection.js, preserving connection_delay sync and per-socket uuid gating.
 */
export function useGameConnection({ groupId, userId, themeId }: Args): void {
  useEffect(() => {
    const socket = connectSocket();
    const store = useGame.getState();
    let ourSocketId = "";
    let msgSeq = 0;

    installBridge(themeId);

    // Visual cues are delayed by the measured audio latency (legacy behaviour).
    const afterDelay = (fn: () => void) => window.setTimeout(fn, audio.connectionDelay);

    const addMessage = (m: ChatMessage) => {
      if (!m.contents && !m.image_url && !m.files) return;
      const name = m.system && m.name === "Reece" ? "Rhys" : m.name;
      store.addMessage(toUiMessage({ ...m, name }, m.user_id === userId, `m${msgSeq++}`));
    };

    socket.on("connect", () => {
      ourSocketId = socket.id ?? "";
      useGame.getState().setConnected(true);
      socket.emit("join_room", groupId);
      socket.emit("join_room", `${groupId}DRM`);
    });
    socket.on("disconnect", () => useGame.getState().setConnected(false));

    socket.on("new_message", (payload) => {
      if (payload.system) {
        afterDelay(() => {
          addMessage(payload);
          if (payload.contents || payload.image_url || payload.files) audio.playNotification();
        });
      } else {
        addMessage(payload);
      }
    });

    socket.on("new_caption", (payload) => afterDelay(() => store.setCaption(payload.contents)));
    socket.on("server_show_temp_caption", (payload) => store.setCaption(payload.text));
    socket.on("clue_disclosed", (payload) => store.setClue(payload.text));

    socket.on("show_files", (individualFiles) =>
      store.setFiles(individualFiles as never),
    );
    socket.on("individual_show_files", (payload) => {
      if (payload.user_uuid === ourSocketId) store.setFiles(payload.payload as never);
    });

    socket.on("about_to_progress", () =>
      store.setPuzzleSecondary(secondaryDefaultPuzzleUrl(themeId)),
    );
    socket.on("have_progressed", () => afterDelay(() => useGame.getState().onProgressed()));

    socket.on("auto_open_pdf", (payload) => afterDelay(() => store.openPdf(pdfPages(payload.file))));
    socket.on("individual_auto_open_pdf", (payload) => {
      if (payload.user_uuid === ourSocketId) store.openPdf(pdfPages(payload.file));
    });
    socket.on("auto_close_pdf", () => afterDelay(() => store.closePdf()));

    socket.on("show_image_cue", (image) => afterDelay(() => store.setCueImage(image)));

    socket.on("load_external_puzzle", (payload) =>
      afterDelay(() => store.setPuzzle(externalPuzzleUrl(themeId, payload.filepath, groupId, userId))),
    );
    socket.on("load_external_puzzle_specific", (payload) => {
      if (payload.effected_uuid === ourSocketId) {
        store.setPuzzle(externalPuzzleUrl(themeId, payload.filepath, groupId, userId));
      }
    });
    socket.on("load_secondary_external_puzzle", (payload) =>
      afterDelay(() =>
        store.setPuzzleSecondary(externalPuzzleUrl(themeId, payload.filepath, groupId, userId)),
      ),
    );
    socket.on("load_secondary_external_puzzle_specific", (payload) => {
      if (payload.effected_uuid === ourSocketId) {
        store.setPuzzleSecondary(externalPuzzleUrl(themeId, payload.filepath, groupId, userId));
      }
    });
    socket.on("hide_external_puzzle", () =>
      afterDelay(() => store.setPuzzle(defaultPuzzleUrl(themeId))),
    );

    socket.on("play_audio", (payload) =>
      afterDelay(() => audio.playAudio(payload.filename, payload.live_metadata)),
    );
    socket.on("play_background_audio", (payload) =>
      audio.playBackgroundAudio(payload.filename, payload.audio_level),
    );
    socket.on("catch_up_audio", (payload) => {
      if (payload.effected_uuid === ourSocketId) {
        audio.playAudioPartThrough(payload.audio_file, payload.position_to_end, payload.live_metadata);
      }
    });

    // Load history, then default the main puzzle screen.
    void (async () => {
      const prev = await api.getMessages(groupId);
      if (prev.success === 1) prev.payload.forEach((row) => addMessage(rowToChatMessage(row)));
      store.setPuzzle(defaultPuzzleUrl(themeId));
      store.setPuzzleSecondary(secondaryDefaultPuzzleUrl(themeId));
    })();

    return () => {
      socket.removeAllListeners();
    };
  }, [groupId, userId, themeId]);
}

function rowToChatMessage(row: {
  user_id: string | null;
  name: string | null;
  contents: string | null;
  image_url: string | null;
  system: boolean;
  files: string | null;
}): ChatMessage {
  return {
    user_id: row.user_id ?? "",
    name: row.name ?? "",
    contents: row.contents ?? "",
    image_url: row.image_url ?? undefined,
    system: row.system,
    files: row.files ?? undefined,
  };
}
